"use client";

import {
  BlogPost,
  FinanceAndAccountingSubcategory,
  FormHeading,
  OtherFormCategory,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import Link from "next/link";
import React, { useState } from "react";
import { IoIosArrowForward } from "react-icons/io";
import Image from "next/image";
import DescriptionExtend from "@/components/blogs/description/DescriptionExtend";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

// Lazy load forms - only loads when needed (below the fold or when modal opens)
const GlobalForm = dynamic(() => import("../forms/GlobalForm"), {
  ssr: false,
  loading: () => (
    <div style={{ minHeight: "460px" }} className="animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="h-10 bg-gray-200 rounded mb-3"></div>
      <div className="h-10 bg-gray-200 rounded mb-3"></div>
      <div className="h-10 bg-gray-200 rounded mb-3"></div>
      <div className="h-10 bg-gray-200 rounded mb-3"></div>
      <div className="h-12 bg-gray-200 rounded mt-4"></div>
    </div>
  ),
});

const EnqueryForm = dynamic(() => import("../forms/EnqueryForm"), {
  ssr: false,
  loading: () => (
    <div style={{ minHeight: "460px" }} className="animate-pulse">
      <div className="h-6 bg-gray-600 rounded w-3/4 mb-4"></div>
      <div className="h-10 bg-gray-600 rounded mb-3"></div>
      <div className="h-10 bg-gray-600 rounded mb-3"></div>
      <div className="h-10 bg-gray-600 rounded mb-3"></div>
      <div className="h-12 bg-gray-600 rounded mt-4"></div>
    </div>
  ),
});

const BlogDetailsPageMobile = ({
  blogData,
  relatedBlogs = [],
}: {
  blogData?: BlogPost;
  relatedBlogs?: BlogPost[];
}) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const pathname = usePathname();
  const redirectionPath = pathname?.split("/")[2];

  const authorImageUrl =
    blogData?.author?.image_url || "/blog/blog_fallback_author_image.avif";
  const authorImageAltText = blogData?.author?.title || "Author Image";
  const publishDate = blogData?.publish_date
    ? new Date(blogData.publish_date)
    : null;
  const modifiedDate = blogData?.modified_date
    ? new Date(blogData.modified_date)
    : null;
  const updatedDate = modifiedDate ? modifiedDate : publishDate;
  const BlogContent = blogData?.blog_content || "";
  const handleFormModalClose = () => {
    setIsFormModalOpen(false);
  };

  const getBreadcrumbInfo = () => {
    if (redirectionPath === "us-jobs-for-nurses") {
      return {
        text: "Nursing",
        url: "/blog/us-jobs-for-nurses?page=1",
        blogUrlPattern: "/blog/us-jobs-for-nurses",
      };
    } else if (redirectionPath === "mbbs") {
      return {
        text: "MBBS",
        url: "/blog/mbbs?page=1",
        blogUrlPattern: "/blog/mbbs",
      };
    } else if (redirectionPath === "accounting") {
      return {
        text: "Accounting",
        url: "/blog/accounting",
        blogUrlPattern: "/blog/accounting",
      };
    } else if (redirectionPath === "school-of-branding-and-advertising") {
      return {
        text: "SoBA",
        url: "/blog/school-of-branding-and-advertising?page=1",
        blogUrlPattern: "/blog/school-of-branding-and-advertising",
      };
    }
  };

  const breadcrumbInfo = getBreadcrumbInfo();

  let courseId;
  let courseTag = "CPA";

  if (blogData?.tags && blogData.tags.length > 0 && blogData.tags[0]?.tags) {
    const tag = blogData.tags[0].tags;
    courseTag = tag;

    if (tag === "CPA") {
      courseId = 1;
    } else if (tag === "CMA") {
      courseId = 2;
    } else if (tag === "USP") {
      courseId = 3;
    } else if (tag === "Nursing") {
      courseId = 34;
    } else if (tag === "Doctors") {
      courseId = 33;
    } else if (tag === "MBA Abroad") {
      courseId = 51;
    } else if (tag === "SoBA") {
      courseId = 54;
    } else {
      courseId = 1;
    }
  } else {
    courseId = 1;
  }

  return (
    <div className="bg-white py-6 px-3 pt-4 w-full">
      <div>
        <div className="flex gap-1 items-center">
          <p className="text-sm font-semibold text-[#39F] leading-[20px]">
            Resources
          </p>
          <IoIosArrowForward className="text-[#39F]" />
          <Link
            href="/blog"
            className="text-sm font-semibold text-[#39F] leading-[20px] !min-h-fit hover:underline !min-w-fit"
          >
            Blog
          </Link>
          <IoIosArrowForward className="text-[#39F]" />
          <Link
            href={breadcrumbInfo?.url || "/blog"}
            className="text-sm font-semibold text-[#39F] leading-[20px] !min-h-fit underline !min-w-fit"
          >
            {breadcrumbInfo?.text}
          </Link>
        </div>
        {/* <div onClick={() => setIsFormModalOpen(true)}>
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/blog/sticky_mobile_banner.webp?tr=w-300,h-200,q-80"
            alt="sticky banner"
            width={300}
            height={200}
            loading="lazy"
            className="w-full rounded-[12px] mt-5"
          />
        </div> */}
        <div className="pt-4">
          <h1 className="tracking-[-0.321px] text-xl font-medium text-[40px] text-black">
            {blogData?.title || "Loading..."}
          </h1>
          <div className="border-t-2 border-black border-b-2 py-2 mt-3 flex gap-2">
            <Image
              src={authorImageUrl}
              alt={authorImageAltText}
              width={32}
              height={32}
              loading="lazy"
              className="rounded-full w-8 h-8"
            />
            <div>
              <p className="text-[#454545] tracking-[-0.4px] font-medium text-xs">
                {blogData?.author?.name}
              </p>
              <div className="flex">
                {updatedDate ? (
                  <p className="text-[#8B8B8B] tracking-[-0.4px] text-xs">
                    {updatedDate.getDate().toString().padStart(2, "0")}-
                    {(updatedDate.getMonth() + 1).toString().padStart(2, "0")}-
                    {updatedDate.getFullYear()}
                  </p>
                ) : (
                  <p></p>
                )}
                <ul className="list-disc pl-6 text-black">
                  <li className="text-[#8B8B8B] tracking-[-0.4px] text-xs">
                    {blogData?.read_time}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="pt-3">
            <DescriptionExtend
              blogContentData={BlogContent}
              blogBannerImage={
                blogData?.banner_url
                  ? `${blogData.banner_url}?tr=w-600,h-auto,q-70,f-webp,pr-true`
                  : ""
              }
            />
          </div>
        </div>
      </div>
      <div
        className="pt-10"
        style={{ minHeight: "520px", contain: "layout style" }}
      >
        <div
          className="w-full max-w-[400px] p-4 border border-white rounded-2xl"
          style={{
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
            boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
            backdropFilter: "blur(28px)",
          }}
        >
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp?tr=w-34,h-38,q-80"
            alt="star group"
            width={34}
            height={38}
            loading="lazy"
            className="absolute top-1 left-0"
          />
          <GlobalForm
            course={courseTag}
            courseId={courseId}
            formType="page"
            pageType={OtherFormCategory.BLOG_PAGE_FORM}
            darkBackground={false}
            submitButtonName="Get in Touch"
            formHeading="bdfj"
            successMessage={SuccessMessageType.BLOG_PAGE_FORM}
          />
        </div>
        <div>
          <h3 className="text-2xl tracking-[-0.96px] font-semibold text-black text-center py-6">
            Read more{" "}
            <span className="font-semibold leading-[94%] overflow-visible bg-gradient-to-r from-[#39f] to-[#ffba08] [background-clip:text] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] pr-1 !tracking-[-0.96px]">
              Posts
            </span>
          </h3>
          <div className="flex flex-col gap-3">
            {relatedBlogs?.map((relatedBlog) => {
              return (
                <Link
                  href={`/blog/${redirectionPath}/${relatedBlog?.slug}`}
                  key={relatedBlog?.id}
                  className="bg-[#272727] rounded-xl flex p-3 gap-2"
                  style={{ backdropFilter: "blur(5px)" }}
                >
                  <Image
                    src={
                      relatedBlog?.banner_url
                        ? `${relatedBlog.banner_url}?tr=w-90,h-90,q-80`
                        : "/blog/blog_fallback_author_image.avif"
                    }
                    alt="related blog image"
                    width={90}
                    height={90}
                    loading="lazy"
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-white line-clamp-2">
                      {relatedBlog?.title}
                    </p>
                    <div className="flex justify-between">
                      <p className="text-[#B0B0B0] text-sm">Published on</p>
                      <p className="text-[#B0B0B0] text-sm">
                        {new Date(relatedBlog.publish_date)
                          .getDate()
                          .toString()
                          .padStart(2, "0")}
                        -
                        {(new Date(relatedBlog.publish_date).getMonth() + 1)
                          .toString()
                          .padStart(2, "0")}
                        -{new Date(relatedBlog.publish_date).getFullYear()}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-full max-w-[430px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModalClose}
              className="text-white text-md md:text-lg absolute right-0 md:right-1 top-0 md:top-1 cursor-pointer z-30"
              aria-label="Close modal"
            >
              ✕
            </button>
            <div className="relative">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp?tr=w-34,h-38,q-80"
                alt="Decorative stars"
                width={34}
                height={38}
                className="absolute top-[-8px] left-[-24px] z-20"
                loading="lazy"
              />

              <EnqueryForm
                course={courseTag}
                courseId={courseId}
                formHeading={FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN}
                formType="page"
                isAStepperForm
                subCategory={FinanceAndAccountingSubcategory.CPA}
                submitButtonName="Request a call back"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetailsPageMobile;
